import type { Event, EventHint } from '@sentry/browser';
import * as WebSentry from '@sentry/browser';

import { Singleton } from '../../common/utils/Singleton';
import { FRUITFUL_ENV, SENTRY_DSN } from '../../config/config';
import { IS_LOCAL } from '../../config/FruitfulEnvironment';
import type { AdminUser } from '../../entities/entityTypes';
import type { ISentry } from './types/Sentry';

const DATADOG_RESPONSE_CLONE_FRAME_PATH =
    '/node_modules/@datadog/browser-core/esm/tools/utils/responseUtils.js';

const isNetworkErrorTypeError = (event: Event, hint: EventHint): boolean => {
    const originalException = hint.originalException;

    if (
        originalException instanceof TypeError &&
        originalException.message === 'network error'
    ) {
        return true;
    }

    return (
        event.exception?.values?.some(
            (exception) =>
                exception.type === 'TypeError' &&
                exception.value === 'network error',
        ) ?? false
    );
};

const hasDatadogResponseCloneFrame = (event: Event): boolean => {
    return (
        event.exception?.values?.some((exception) =>
            exception.stacktrace?.frames?.some(
                (frame) =>
                    frame.filename?.includes(
                        DATADOG_RESPONSE_CLONE_FRAME_PATH,
                    ) ?? false,
            ),
        ) ?? false
    );
};

const isIgnoredDatadogRumResponseCloneError = (
    event: Event,
    hint: EventHint,
): boolean => {
    return (
        isNetworkErrorTypeError(event, hint) &&
        hasDatadogResponseCloneFrame(event)
    );
};

class SentryFacade extends Singleton implements ISentry {
    public readonly Sentry: typeof WebSentry;

    constructor() {
        super();
        this.Sentry = WebSentry;
    }

    public init(): void {
        if (!this.isEnabled()) {
            return;
        }

        this.Sentry.init({
            dsn: SENTRY_DSN,
            environment: FRUITFUL_ENV,
            integrations: [
                this.Sentry.browserTracingIntegration(),
                this.Sentry.extraErrorDataIntegration({
                    depth: 8,
                    captureErrorCause: true,
                }),
            ],
            tracesSampleRate: 0.1,
            beforeSend: (event, hint) => {
                if (isIgnoredDatadogRumResponseCloneError(event, hint)) {
                    return null;
                }

                return event;
            },
        });

        const scope = this.Sentry.getCurrentScope();
        scope.setTag('app', 'grove-app');
        scope.setTag('platform', 'web');
    }

    public isEnabled(): boolean {
        return !IS_LOCAL && SENTRY_DSN != null;
    }

    public identifyUser(user: AdminUser): void {
        if (!this.isEnabled()) {
            return;
        }
        this.Sentry.setUser({
            id: user.id ?? undefined,
            email: user.email,
        });
    }
}

export default SentryFacade.getInstance();
